import { ErrorMessage, Field, Form, Formik } from "formik";
import { Modal } from "react-bootstrap";
import { FaTimes } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import axios from "axios";
import { toast } from "react-toastify";
import { registerEndUser } from "../../features/user/userSlice";
import { base_url } from "../../utils/axiosConfig";

const EndUserSignUp = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(0);
  const [isRegistered, setIsRegistered] = useState(false);
  const [packages, setPackages] = useState([]);
  const [referredUserData, setReferredUserData] = useState(null);
  const timeoutRef = useRef(null);

  // Debounce function for referral code validation
  const debounce = (func, delay) => {
    return (...args) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => func(...args), delay);
    };
  };

  // Fetch referred user data
  const fetchReferredUserData = async (referralCode) => {
    if (!referralCode) {
      setReferredUserData(null);
      return;
    }
    try {
      const response = await axios.get(
        `${base_url}user/getReferredUserData?referralCode=${referralCode}`,
      );
      setReferredUserData(response.data);
    } catch (error) {
      console.error("Error fetching referred user data:", error);
      setReferredUserData(null);
    }
  };

  const debouncedFetchReferredUserData = debounce(fetchReferredUserData, 500);

  // Fetch packages for the commented-out package selection step
  useEffect(() => {
    if (currentStep === 3 && show) {
      axios
        .get(`${base_url}v1/package/fetchAllpackage`)
        .then((response) => {
          setPackages(response.data.data);
        })
        .catch((error) => {
          console.error("Error fetching packages:", error);
        });
    }
  }, [currentStep, show]);

  // Cleanup debounce timeout
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Validation schemas for each step
  const signupSchemas = Yup.object({
    firstname: Yup.string().required("First Name is required"),
    lastname: Yup.string().required("Last Name is required"),
    dob: Yup.date().required("DOB is required"),
    gender: Yup.string().required("Gender is required"),
    bloodGroup: Yup.string().required("Blood Group is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    mobile: Yup.string()
      .matches(/^[0-9]+$/, "Not a Valid Phone Number")
      .required("Phone is required"),
    profilePhoto: Yup.mixed().required("Profile photo is required"),
    address: Yup.object({
      address_line: Yup.string().required("Street address is required"),
      city: Yup.string().required("City is required"),
      district: Yup.string().required("District is required"),
      state: Yup.string().required("State is required"),
      country: Yup.string().required("Country is required"),
      pincode: Yup.string()
        .matches(/^[0-9]+$/, "Not a Valid Pincode")
        .required("Pincode is required"),
    }),
  });

  const initialSignupValues = {
    firstname: "",
    lastname: "",
    dob: "",
    gender: "",
    bloodGroup: "",
    email: "",
    mobile: "",
    profilePhoto: null,
    govProof: null,
    password: "",
    address: {
      address_line: "",
      district: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
    },
    // referralCode: "",
    packageId: "",
  };

  const steps = ["Personal Information", "Address Details"];

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="modal-90w md:modal-lg"
      backdrop="static"
    >
      <Modal.Body className="p-4 md:p-6">
        <div className="relative bg-white rounded-lg">
          <button
            onClick={onHide}
            className="absolute top-2 right-2 bg-transparent border-0"
          >
            <FaTimes size={16} color="#666" />
          </button>
          {!isRegistered && (
            <div className="text-center mb-4">
              <h3 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#00C70D] to-[#005AD0] bg-clip-text text-transparent">
                User Sign Up
              </h3>
              <p className="text-gray-500 text-xs md:text-sm">
                Fill in your details to sign up as a Member
              </p>
            </div>
          )}
          {isRegistered ? (
            <div className="text-center py-6">
              <h2 className="text-lg md:text-xl font-bold text-green-600">
                Registration Successful
              </h2>
              <svg
                className="w-10 h-10 mx-auto text-green-600 mt-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          ) : (
            <>
              {/* <div className="mb-6">
                <div className="flex justify-between mb-4">
                  {steps.map((step, index) => (
                    <div
                      key={index}
                      className={`text-sm font-semibold ${
                        index <= currentStep ? "text-[#00C70D]" : "text-gray-400"
                      }`}
                    >
                      {step}
                    </div>
                  ))}
                </div>
              </div> */}

              <Formik
                initialValues={initialSignupValues}
                validationSchema={signupSchemas}
                onSubmit={async (values, { setSubmitting, resetForm }) => {
                  try {
                    const formData = new FormData();
                    formData.append("firstname", values.firstname);
                    formData.append("lastname", values.lastname);
                    formData.append("dob", values.dob);
                    formData.append("gender", values.gender);
                    formData.append("bloodGroup", values.bloodGroup);
                    formData.append("email", values.email);
                    formData.append("mobile", values.mobile);
                    formData.append(
                      "address.address_line",
                      values.address.address_line,
                    );
                    formData.append(
                      "address.district",
                      values.address.district,
                    );
                    formData.append("address.city", values.address.city);
                    formData.append("address.state", values.address.state);
                    formData.append("address.country", values.address.country);
                    formData.append("address.pincode", values.address.pincode);
                    // formData.append("referralCode", values.referralCode);
                    formData.append("profilePhoto", values.profilePhoto);
                    formData.append("userType", "member");
                    // formData.append("govProof", values.govProof);
                    // formData.append("packageId", values.packageId);

                    await dispatch(registerEndUser(formData)).unwrap();
                    setIsRegistered(true);
                    resetForm();
                    setTimeout(() => {
                      onHide();
                      toast.success("Registration successful! Please sign in.");
                    }, 2000);
                  } catch (error) {
                    console.error("Registration error:", error);
                    toast.error(error?.message || "Registration failed");
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {({ isSubmitting, setFieldValue, values }) => (
                  <Form className="flex flex-col gap-4">
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs md:text-xs font-semibold">
                            First Name<span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="firstname"
                            placeholder="Enter First Name"
                            className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                          <ErrorMessage
                            name="firstname"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs md:text-xs font-semibold">
                            Last Name<span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="lastname"
                            placeholder="Enter Last Name"
                            className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                          <ErrorMessage
                            name="lastname"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs md:text-xs font-semibold">
                            Email<span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="email"
                            placeholder="Enter Email"
                            className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                          <ErrorMessage
                            name="email"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs md:text-xs font-semibold">
                            Phone Number<span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="mobile"
                            placeholder="Enter Phone Number"
                            className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                          <ErrorMessage
                            name="mobile"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs md:text-xs font-semibold">
                            Gender<span className="text-red-500">*</span>
                          </label>
                          <Field
                            as="select"
                            name="gender"
                            className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                          >
                            <option value="">Select Gender</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                          </Field>
                          <ErrorMessage
                            name="gender"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs md:text-xs font-semibold">
                            Blood Group<span className="text-red-500">*</span>
                          </label>
                          <Field
                            as="select"
                            name="bloodGroup"
                            className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                          >
                            <option value="">Select Blood Group</option>
                            <option value="A+">A+</option>
                            <option value="A-">A-</option>
                            <option value="B+">B+</option>
                            <option value="B-">B-</option>
                            <option value="AB+">AB+</option>
                            <option value="AB-">AB-</option>
                            <option value="O+">O+</option>
                            <option value="O-">O-</option>
                          </Field>
                          <ErrorMessage
                            name="bloodGroup"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs md:text-xs font-semibold">
                            Date of Birth<span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="date"
                            name="dob"
                            className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                            max={new Date().toISOString().split("T")[0]}
                          />
                          <ErrorMessage
                            name="dob"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs md:text-xs font-semibold">
                            Upload Profile Picture
                            <span className="text-red-500">*</span>
                          </label>
                          <button
                            type="button"
                            className="w-full mt-1 p-2 bg-[#00C70D] text-white rounded-lg text-sm"
                            onClick={() =>
                              document.getElementById("profilePhoto").click()
                            }
                          >
                            Upload
                          </button>
                          <input
                            type="file"
                            id="profilePhoto"
                            className="hidden"
                            onChange={(event) => {
                              setFieldValue(
                                "profilePhoto",
                                event.target.files[0],
                              );
                            }}
                          />
                          {values.profilePhoto && (
                            <div className="mt-1 text-xs text-green-600">
                              {values.profilePhoto.name || "File uploaded"}
                            </div>
                          )}
                          <ErrorMessage
                            name="profilePhoto"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <div>
                          <label className="block text-xs md:text-xs font-semibold">
                            Street Address
                            <span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="address.address_line"
                            placeholder="Enter Street Address"
                            className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                          <ErrorMessage
                            name="address.address_line"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs md:text-xs font-semibold">
                            City<span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="address.city"
                            placeholder="Enter City"
                            className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                          <ErrorMessage
                            name="address.city"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs md:text-xs font-semibold">
                            District<span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="address.district"
                            placeholder="Enter District"
                            className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                          <ErrorMessage
                            name="address.district"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs md:text-xs font-semibold">
                            State<span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="address.state"
                            placeholder="Enter State"
                            className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                          <ErrorMessage
                            name="address.state"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs md:text-xs font-semibold">
                            Country<span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="address.country"
                            placeholder="Enter Country"
                            className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                          <ErrorMessage
                            name="address.country"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs md:text-xs font-semibold">
                            Pincode<span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="address.pincode"
                            placeholder="Enter Pincode"
                            className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                          <ErrorMessage
                            name="address.pincode"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                      </div>
                    </>

                    <div className="mt-6 flex justify-center gap-3">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-28 md:w-36 py-2 bg-[#00C70D] text-white font-semibold text-sm md:text-base rounded-md shadow-md flex items-center justify-center ${
                          isSubmitting ? "opacity-75 cursor-not-allowed" : ""
                        }`}
                      >
                        {isSubmitting ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 md:h-5 md:w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Loading...
                          </>
                        ) : (
                          "Register"
                        )}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default EndUserSignUp;
